/**
 * Free-flight speed resolution: cruise setting, proximity ceiling, and the
 * pilot's ability to push through that ceiling deliberately.
 *
 * Pure and self-contained on purpose — no camera, no game, no frame loop — so
 * the rules below can be tested as rules rather than inferred from flying
 * around. `FreeFlightCamera` holds one of these and does nothing else with
 * speed.
 *
 * THE PROBLEM. Crossing the system wants 1e7 m/s; inspecting a landing leg
 * wants 1e-2. Automatically slowing near a surface fixes arriving, and
 * restoring the setting on departure fixes leaving, but between those two a
 * pilot who genuinely wants to move fast *while* near a planet had no way to
 * say so: the wheel simply stopped having any visible effect once the ceiling
 * was below the cruise setting.
 *
 * THE RULES, in full:
 *
 *   1. Away from anything, the wheel sets the cruise speed and the camera flies
 *      at it. This is the original behaviour and nothing here changes it.
 *
 *   2. Near a surface a ceiling applies (see `FreeFlightCamera.proximitySpeedLimit`)
 *      and the camera flies at the lesser of cruise and ceiling.
 *
 *   3. Scrolling UP while the ceiling is actually holding the camera back arms
 *      an override. The override starts AT THE CEILING rather than jumping
 *      straight back to cruise — arriving at 1e7 and being capped to 1e5 should
 *      not mean one notch of wheel returns 1e7. From there the wheel moves the
 *      override, so pushing through the ceiling feels like every other speed
 *      change: continuous, a notch at a time.
 *
 *   4. The cruise setting is NEVER written while overriding. That is what makes
 *      departure free: the override is local to this visit, and the speed the
 *      pilot set out with is still there underneath it.
 *
 *   5. The override clears itself the moment it stops meaning anything — when
 *      the pilot walks it back down to the ceiling, or when the ceiling rises
 *      past it on departure. So each arrival starts supervised again, and an
 *      override can never silently persist into the next planet.
 */
import { clamp } from '@/core/math/scalar';

/** Inclusive bounds on any speed exponent, as log10(m/s). */
export interface SpeedExponentBounds {
  min: number;
  max: number;
}

export interface FreeFlightSpeedState {
  /**
   * The pilot's cruise setting, log10(m/s). Only the wheel writes it, and only
   * while no override is in force. Persisted across sessions.
   */
  readonly cruiseExponent: number;
  /**
   * Local override while near a surface, log10(m/s), or null when the ceiling
   * is being respected. Deliberately NOT persisted: it belongs to one visit.
   */
  readonly overrideExponent: number | null;
}

export interface ResolvedSpeed {
  /** What the camera actually flies at, m/s. */
  speed: number;
  /** True while the camera is flying slower than the cruise setting asks. */
  limited: boolean;
  /** True while the pilot is deliberately holding above the ceiling. */
  overriding: boolean;
}

/** `log10` of a ceiling, with `Infinity` surviving as `Infinity`. */
function exponentOf(limit: number): number {
  if (!Number.isFinite(limit)) return Infinity;
  return Math.log10(Math.max(limit, Number.MIN_VALUE));
}

export function createSpeedState(cruiseExponent: number): FreeFlightSpeedState {
  return { cruiseExponent, overrideExponent: null };
}

/** What the camera should fly at, given the state and the current ceiling. */
export function resolveSpeed(state: FreeFlightSpeedState, limit: number): ResolvedSpeed {
  const cruise = Math.pow(10, state.cruiseExponent);
  if (state.overrideExponent !== null) {
    const speed = Math.pow(10, state.overrideExponent);
    return { speed, limited: speed < cruise, overriding: true };
  }
  const speed = Math.min(cruise, limit);
  return { speed, limited: speed < cruise, overriding: false };
}

/**
 * Applies wheel travel, in decades. Returns a new state; never mutates.
 *
 * Which number the wheel moves depends on whether an override is in force —
 * see rules 3 and 4. This is the only place either exponent changes.
 */
export function applyWheel(
  state: FreeFlightSpeedState,
  decades: number,
  limit: number,
  bounds: SpeedExponentBounds,
): FreeFlightSpeedState {
  const capExponent = exponentOf(limit);

  if (state.overrideExponent !== null) {
    const next = clamp(state.overrideExponent + decades, bounds.min, bounds.max);
    // Walked back down to the ceiling: stop overriding rather than sitting at
    // an override that happens to equal the cap, so the pilot is supervised
    // again without having to do anything else.
    if (next <= capExponent) return { ...state, overrideExponent: null };
    return { ...state, overrideExponent: next };
  }

  const limited = state.cruiseExponent > capExponent;
  if (decades > 0 && limited) {
    // Arm from the ceiling, not from cruise: see rule 3.
    return {
      ...state,
      overrideExponent: clamp(capExponent + decades, bounds.min, bounds.max),
    };
  }

  return { ...state, cruiseExponent: clamp(state.cruiseExponent + decades, bounds.min, bounds.max) };
}

/**
 * Drops an override the ceiling no longer binds — the departure case (rule 5).
 * Call once per frame with the current ceiling; a no-op otherwise.
 */
export function settleOverride(
  state: FreeFlightSpeedState,
  limit: number,
): FreeFlightSpeedState {
  if (state.overrideExponent === null) return state;
  if (exponentOf(limit) >= state.overrideExponent) return { ...state, overrideExponent: null };
  return state;
}
